import { Settings } from 'lucide-react'
import React, { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FontStyle, Text } from '~/components/core'
import { dimensions, spacing } from '~/constants/theme'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import { desktop } from '~/styles'

import { MessageSnippet } from '@mangovoice/types'

import { backgroundBoxShadow } from './CommonStyles'

const SnippetsWrapper = styled.div`
  width: 100%;
  max-height: 224px;
  border-top-left-radius: ${dimensions.borderRadiusSM};
  border-top-right-radius: ${dimensions.borderRadiusSM};
  ${backgroundBoxShadow};
  position: absolute;
  bottom: 100%;
  background-color: var(--chakra-colors-bg-panel);
`

const SnippetsHeader = styled.div`
  width: 100%;
  padding: ${spacing.spacingSM} ${spacing.spacingLG};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: ${dimensions.borderRadiusSM};
  border-top-right-radius: ${dimensions.borderRadiusSM};
  background-color: var(--chakra-colors-fg-subtle);
`

const SettingsButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--chakra-colors-fg-muted);
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--chakra-colors-border-default);
  }

  &:active {
    background-color: var(--chakra-colors-fg-subtle);
  }

  /* fg.default keeps 3:1 against the fg.subtle header in light and dark mode. */
  &:focus-visible {
    outline: 2px solid var(--chakra-colors-fg-default);
    outline-offset: 2px;
  }
`

const SnippetListWrapper = styled.div`
  width: 100%;
  max-height: 180px;
  overflow: auto;
  border-left: 1px solid var(--chakra-colors-border-default);
  border-right: 1px solid var(--chakra-colors-border-default);
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: ${desktop.scrollbar}px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${desktop.scrollbar}px;
    -webkit-box-shadow: inset 0px 4px 14px rgba(0, 0, 0, 0.1);
    background-color: var(--chakra-colors-border-subtle);
  }
`

const SnippetItemWrapper = styled.div<{ active: boolean }>`
  width: 100%;
  padding: ${spacing.spacingLG};
  display: flex;
  align-items: center;
  border-top: 1px solid var(--chakra-colors-border-subtle);
  background-color: ${(props) =>
    props.active
      ? 'var(--chakra-colors-brand-subtle)'
      : 'var(--chakra-colors-bg-panel)'};
  cursor: pointer;
  &:focus {
    outline: none;
  }
  &:hover {
    background-color: var(--chakra-colors-brand-subtle);
  }
`

const TruncateChild = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: ${spacing.spacingMD};
  display: block;
`

interface SnippetPopupProps {
  data: Array<MessageSnippet>
  position: number
  onHover: (idx: number) => void
  onNavigate: () => void
  onSelect: (snippet: MessageSnippet) => void
  listId?: string
  listRef: RefObject<HTMLDivElement>
  settingsButtonRef?: RefObject<HTMLButtonElement | null>
  onShouldClose?: () => void
}
const SnippetPopup = ({
  data,
  position,
  onHover,
  onNavigate,
  onSelect,
  listId,
  listRef,
  settingsButtonRef,
  onShouldClose,
}: SnippetPopupProps) => {
  const { t } = useTranslation()
  const handleMouseOver = (idx: number, e: React.MouseEvent) => {
    e.preventDefault()
    onHover(idx)
  }
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  useUserClickedExternalElements([wrapperRef.current!], true, () => {
    onShouldClose?.()
  })

  return (
    <SnippetsWrapper ref={wrapperRef}>
      <SnippetsHeader>
        <Text
          fontStyle={FontStyle.Normal}
          text={t('chatMenu.label')}
          color="var(--chakra-colors-brand-contrast)"
        />
        <SettingsButton
          ref={settingsButtonRef}
          type="button"
          aria-label={t('chatMenu.settings', 'Snippet settings')}
          onClick={onNavigate}
        >
          <Settings size={20} aria-hidden="true" />
        </SettingsButton>
      </SnippetsHeader>
      <SnippetListWrapper
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={t('chatMenu.label')}
      >
        {data.map((snippet, idx) => (
          <SnippetItemWrapper
            tabIndex={-1}
            role="option"
            aria-selected={position === idx}
            id={listId ? `${listId}-option-${idx}` : undefined}
            key={`snippet-${snippet.id}`}
            active={position === idx}
            onClick={() => onSelect(snippet)}
            onMouseOver={(e) => handleMouseOver(idx, e)}
          >
            <Text
              as="span"
              fontStyle={FontStyle.bSmall}
              text={snippet.name}
              color="brand.subtle"
            />

            <Text
              as="span"
              fontStyle={FontStyle.Tiny}
              color="var(--chakra-colors-fg-subtle)"
              style={{ overflow: 'hidden' }}
            >
              <TruncateChild>{snippet.body}</TruncateChild>
            </Text>
          </SnippetItemWrapper>
        ))}
      </SnippetListWrapper>
    </SnippetsWrapper>
  )
}

export default SnippetPopup
